import { useEffect, useRef } from 'react';
import {
    IAgentSearchCachePayload,
    TGoogleSource,
    IIndexedGoogleSource,
    TReference,
    TSummaryMetaData,
    TGoogleApiRawResponse,
    TSummary,
    TResults,
    TDocument,
    TDerivedStructData,
    TSnippets
} from '../types/responses/GoogleAiSearchResponse';
import { useApiTokenContext } from '../providers/ApiTokenProvider/ApiTokenProvider';
import { resolveAiSearchOption } from '../utils/aiSearchOptions';

export type { IAgentSearchCachePayload, TGoogleSource, IIndexedGoogleSource };

export const AI_SEARCH_MAX_SUMMARY_SOURCES = 5;
export const AI_SEARCH_CITATION_REGEX = /\[(\s*\d+(?:\s*,\s*\d+)*)\]/g;
const toArray = <T = any>(value: unknown): T[] => (Array.isArray(value) ? value : []);

const getFirstNonEmptyString = (...candidates: unknown[]): string => {
    const candidate = candidates.find((value) => typeof value === 'string' && value.trim());
    return typeof candidate === 'string' ? candidate : '';
};

export const parseCitationNumbers = (value = ''): number[] => {
    return value
        .split(',')
        .map((entry) => Number(entry.trim()))
        .filter((entry) => Number.isInteger(entry) && entry > 0);
};

export const getSummaryCitationsMap = (
    sources: IIndexedGoogleSource[],
): Map<number, IIndexedGoogleSource> => {
    return new Map(sources.map((source) => [source.citationIndex, source]));
};

const normalizeGoogleSource = (source: IIndexedGoogleSource): TGoogleSource | null => {
    const title = String(source?.title ?? '').trim();
    const link = String(source?.link ?? '').trim();
    if (!title || !link) {
        return null;
    }

    return {
        title,
        link,
        snippet: String(source?.snippet ?? ''),
    };
};

const normalizeIndexedSource = (source: IIndexedGoogleSource, fallbackIndex: number): IIndexedGoogleSource | null => {
    const normalizedSource = normalizeGoogleSource(source);
    if (!normalizedSource) {
        return null;
    }

    const parsedIndex = Number(source?.citationIndex);
    return {
        ...normalizedSource,
        citationIndex: Number.isInteger(parsedIndex) && parsedIndex > 0
            ? parsedIndex
            : fallbackIndex,
    };
};

const normalizeGoogleSources = (sources: IIndexedGoogleSource[]): TGoogleSource[] => {
    return sources
        .map((source) => normalizeGoogleSource(source))
        .filter((source): source is TGoogleSource => Boolean(source));
};

export const normalizeIndexedSources = (
    sources: IIndexedGoogleSource[],
    maxSources = Number.POSITIVE_INFINITY,
): IIndexedGoogleSource[] => {
    return sources
        .slice(0, maxSources)
        .map((source, index) => normalizeIndexedSource(source, index + 1))
        .filter((source): source is IIndexedGoogleSource => Boolean(source));
};

const getSummaryTextFromResponse = (summary: TSummary): string => String(
    summary?.summaryText
    ?? summary?.summaryWithMetadata?.summary
    ?? '',
).trim();

const shouldShowNextSteps = (summary: TSummary): boolean => {
    const skippedReasons = summary?.summarySkippedReasons;
    const reasons = Array.isArray(skippedReasons)
        ? skippedReasons
        : typeof skippedReasons === 'string'
            ? [skippedReasons]
            : [];

    return (reasons.includes('NO_RELEVANT_CONTENT') || 
            reasons.includes('OUT_OF_DOMAIN_QUERY_IGNORED') ||
            reasons.includes('JAIL_BREAKING_QUERY_IGNORED') ||
            reasons.includes('ADVERSARIAL_QUERY_IGNORED') 
            )
}

const getAgentSnippetText = (snippets: TSnippets[]): string => {
    if (!Array.isArray(snippets)) {
        return '';
    }

    return snippets
        .map((snippet: TSnippets) => {
            if (typeof snippet === 'string') {
                return snippet;
            }

            return getFirstNonEmptyString(snippet?.snippet, snippet?.snippetText);
        })
        .filter(Boolean)
        .join(' ');
};

const mapAgentSearchResult = (result: TResults ): TGoogleSource | null => {
    const document: TDocument = result?.document ?? ({} as TDocument);
    const derivedStructData = document?.derivedStructData ?? ({} as TDerivedStructData);
    const linkCandidate = getFirstNonEmptyString(
        derivedStructData?.link,
        derivedStructData?.url,
        derivedStructData?.uri,
        result?.link,
        result?.url,
    );

    const titleCandidate = getFirstNonEmptyString(
        derivedStructData?.title,
        result?.title,
        linkCandidate,
    );

    const snippetCandidate = getFirstNonEmptyString(
        getAgentSnippetText(derivedStructData?.snippets),
        derivedStructData?.snippets,
        result?.snippet,
    );

    if (!linkCandidate || !titleCandidate) {
        return null;
    }

    return {
        title: titleCandidate,
        link: linkCandidate,
        snippet: snippetCandidate ?? '',
    };
};

const getAgentSummarySources = (summary: TSummary): IIndexedGoogleSource[] => {
    const summaryWithMetadata: TSummaryMetaData = summary?.summaryWithMetadata ?? ({} as TSummaryMetaData);
    const references: TReference[] = toArray(summaryWithMetadata?.references);

    const referenceSources : IIndexedGoogleSource[] = references.map((reference, index) => ({
         title: reference?.title ?? reference?.uri ?? `Source ${index + 1}`,
         link: reference?.uri ?? '',
         snippet: '',
         citationIndex: index + 1,
    }));

    if (!referenceSources.length) {
        return [];
    }

    return referenceSources.slice(0, AI_SEARCH_MAX_SUMMARY_SOURCES);
};

export const buildAgentSearchPayload = (data: TGoogleApiRawResponse): IAgentSearchCachePayload => {
    const mappedResults = toArray(data?.results).filter(
        item => item?.retrievalSignals?.semanticRelevanceScore !== 'NaN'
    )
    .map(mapAgentSearchResult)
    .filter((result: TGoogleSource | null): result is TGoogleSource => Boolean(result));

    const summary = data?.summary ?? {};
    const summaryText = getSummaryTextFromResponse(summary);
    const summarySources =summaryText ? getAgentSummarySources(summary) : [];
    const showNoResultsVariant = shouldShowNextSteps(summary) 
        || summary?.summaryText?.includes('A summary could not be generated') 
        || summary?.summaryText?.includes('I couldn\'t find information about that in the available content');

    return {
        totalResults: Number(data?.totalSize ?? mappedResults.length),
        results: mappedResults,
        summaryText,
        summarySources,
        showNoResultsVariant,
    };
};

export const parseAgentSearchCachePayload = (
    cachedValue: string,
    requireSummaryText: boolean,
): IAgentSearchCachePayload | null => {
    try {
        const parsed: IAgentSearchCachePayload = JSON.parse(cachedValue);
        const totalResults = Number(parsed?.totalResults);
        const results = normalizeGoogleSources(toArray(parsed?.results));

        const showNoResultsVariant = parsed?.summaryText?.includes('A summary could not be generated') ||
            parsed?.summaryText?.includes('I couldn\'t find information about that in the available content');

        if (!Number.isFinite(totalResults) || totalResults < 0 || results.length === 0) {
            return null;
        }

        const summaryText = typeof parsed?.summaryText === 'string' ? parsed.summaryText.trim() : '';
        if (requireSummaryText && !summaryText) {
            return null;
        }

        return {
            totalResults,
            results,
            summaryText,
            summarySources: normalizeIndexedSources(toArray(parsed?.summarySources)),
            appendTopSearches: Boolean(parsed?.appendTopSearches),
            showNoResultsVariant
        };
    } catch {
        return null;
    }
};

const useAiSearch = () => {

    const { getTokenString, apiTokenMap } = useApiTokenContext();
    const discoveryApiKey = useRef<string>(null);

    useEffect(() => {
        if (apiTokenMap && Object.keys(apiTokenMap).length > 0) {
            discoveryApiKey.current = getTokenString('vertexSearchKey');
        }
    }, [apiTokenMap]);

    const getSearchResults = async (query: string, offset?: number) => {
        try {

            if (!discoveryApiKey.current || !query) {
                throw Error('Invalid request');
            }

            const routeConfig = resolveAiSearchOption(window.location.pathname);

            const endpoint = routeConfig.dataStoreId
                ? `dataStores/${routeConfig.dataStoreId}`
                : `engines/${routeConfig.engineId}`;

            const requestUrl = `https://discoveryengine.googleapis.com/v1/projects/${routeConfig.projectId}/locations/${routeConfig.location}/collections/${routeConfig.collectionId}/${endpoint}/servingConfigs/${routeConfig.servingConfigId}:searchLite?key=${discoveryApiKey.current}`;

            const servingConfig = `projects/${routeConfig.projectId}/locations/${routeConfig.location}/collections/${routeConfig.collectionId}/${endpoint}/servingConfigs/${routeConfig.servingConfigId}`;

            const requestBody = {
                query: query,
                servingConfig,
                pageSize: 10,
                offset: offset,
                contentSearchSpec: {
                    snippetSpec: {
                        returnSnippet: true,
                    },
                    summarySpec: {
                        summaryResultCount: routeConfig.summaryResultCount,
                        includeCitations: true,
                        ignoreAdversarialQuery: true,
                        ignoreNonSummarySeekingQuery: false,
                        ignoreJailBreakingQuery: true,
                        ignoreLowRelevantContent: true,
                        ...(routeConfig.preamble ? {
                            modelPromptSpec: {
                                preamble: routeConfig.preamble,
                            },
                        } : {}),
                        modelSpec: {
                            version: routeConfig.modelVersion,
                        },
                        languageCode: 'en-US',
                        useSemanticChunks: true,
                    },
                },
                languageCode: 'en-US',
                safeSearch: true,
            };

            const response = await fetch(requestUrl, {
                method: 'POST',
                body: JSON.stringify(requestBody),
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            return response;

        } catch (error) {
            console.error('Error calling discovery api: ', error);
        }
    };

    return { getSearchResults };

};

export const parseResponseMarkdown = (unparsed: string) : string => {
  return unparsed.replace(/\*\*(.*?)\*\*/g,'<b>$1</b>');
}

export default useAiSearch;
